import { useEffect, useState } from "react";
import api, { fmtDateTime } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../lib/auth";
import { Plus, Check, PackageCheck, Package, X, Trash2 } from "lucide-react";

const STATUS = {
  bekliyor: { label: "Bekliyor", color: "bg-amber-500 text-slate-900" },
  hazirlandi: { label: "Hazırlandı", color: "bg-blue-500 text-white" },
  teslim: { label: "Teslim Edildi", color: "bg-emerald-600 text-white" },
  iptal: { label: "İptal", color: "bg-slate-400 text-white" },
};

const emptyReq = { part_id: "", part_name: "", quantity: 1, note: "", work_order_id: "", work_order_no: "" };

export default function PartRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [parts, setParts] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyReq);

  const canManageStock = user?.permissions?.includes("parts");
  const canRequest = user?.permissions?.includes("part_requests") || user?.permissions?.includes("service");

  const load = () => api.get(`/part-requests${tab !== "all" ? `?status=${tab}` : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    api.get("/parts").then((r) => setParts(r.data)).catch(() => {});
    api.get("/work-orders").then((r) => setWorkOrders(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.part_name) return toast.error("Parça adı zorunlu");
    try {
      await api.post("/part-requests", { ...form, quantity: Number(form.quantity) || 1 });
      toast.success("Talep gönderildi. Depoya bildirim düştü."); setOpen(false); setForm(emptyReq); load();
    } catch { toast.error("Talep gönderilemedi"); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/part-requests/${id}/status`, { status });
      const map = { hazirlandi: "Hazırlandı olarak işaretlendi", teslim: "Teslim edildi ve stok düşüldü", iptal: "İptal edildi" };
      toast.success(map[status] || "Güncellendi"); load();
    } catch { toast.error("İşlem başarısız"); }
  };

  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/part-requests/${id}`); toast.success("Silindi"); load(); } };

  return (
    <div className="space-y-5" data-testid="part-requests-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Servis ↔ Depo</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Parça Talepleri</h1>
        </div>
        {canRequest && (
          <Button className="bg-[#D97706] hover:bg-[#B45309] text-white gap-2" onClick={() => { setForm(emptyReq); setOpen(true); }} data-testid="add-pr-btn">
            <Plus size={16} /> Yeni Talep
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="all" data-testid="pr-tab-all">Tümü</TabsTrigger>
          {Object.entries(STATUS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`pr-tab-${k}`}>{v.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {items.map((r) => (
          <Card key={r.id} className="border-slate-200 tp-fade" data-testid={`pr-card-${r.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs text-slate-500 font-mono">{r.pr_no}</div>
                  <div className="font-bold text-slate-900">{r.part_name}</div>
                  <div className="text-xs text-slate-500">{r.quantity} adet · Talep: {r.requested_by_name}</div>
                  {r.work_order_no && <div className="text-xs text-slate-500">İş Emri: <span className="font-mono">{r.work_order_no}</span></div>}
                </div>
                <Badge className={STATUS[r.status]?.color}>{STATUS[r.status]?.label}</Badge>
              </div>
              {r.note && <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">{r.note}</div>}
              <div className="text-[10px] text-slate-400">{fmtDateTime(r.created_at)}</div>
              <div className="flex gap-1 pt-1 flex-wrap">
                {r.status === "bekliyor" && canManageStock && (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={() => setStatus(r.id, "hazirlandi")} data-testid={`pr-prep-${r.id}`}>
                    <Package size={14} /> Hazırla
                  </Button>
                )}
                {r.status === "hazirlandi" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => setStatus(r.id, "teslim")} data-testid={`pr-deliver-${r.id}`}>
                    <PackageCheck size={14} /> Teslim Al
                  </Button>
                )}
                {["bekliyor", "hazirlandi"].includes(r.status) && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "iptal")} data-testid={`pr-cancel-${r.id}`}>
                    <X size={14} className="text-rose-600" />
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => remove(r.id)} data-testid={`pr-delete-${r.id}`}>
                  <Trash2 size={14} className="text-rose-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Kayıt yok.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Parça Talebi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Parça (stoktan seç)</Label>
              <Select value={form.part_id || ""} onValueChange={(v) => { const p = parts.find((x) => x.id === v); setForm({ ...form, part_id: v, part_name: p?.name || "" }); }}>
                <SelectTrigger data-testid="pr-part"><SelectValue placeholder="Stoktan seç" /></SelectTrigger>
                <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.quantity} adet)</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">veya elle parça adını yazın:</div>
              <Input className="mt-1" value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} placeholder="Örn: Yağ filtresi" data-testid="pr-name" />
            </div>
            <div><Label>Adet</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="pr-qty" /></div>
            <div>
              <Label>İlgili İş Emri (opsiyonel)</Label>
              <Select value={form.work_order_id || ""} onValueChange={(v) => { const w = workOrders.find((x) => x.id === v); setForm({ ...form, work_order_id: v, work_order_no: w?.wo_no || "" }); }}>
                <SelectTrigger data-testid="pr-wo"><SelectValue placeholder="İş emri seç" /></SelectTrigger>
                <SelectContent>{workOrders.map((w) => <SelectItem key={w.id} value={w.id}>{w.wo_no} · {w.customer_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Açıklama / Aciliyet</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pr-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#D97706] hover:bg-[#B45309]" onClick={submit} data-testid="save-pr-btn">Talep Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
